import React, { ChangeEvent, ClipboardEvent, FunctionComponent } from "react";
import { Form, Input } from "antd";
import type { Rule } from "antd/lib/form";
import FormLabel from "../FormLabel";
import styles from "../../BiformLayout.module.scss";
import { QUESTION_TYPES } from "../../constants";
import { V2Question } from "../../helper";

/** The currency the stipend is entered in. core-api stores the amount with it (`stipendCurrency`, INR). */
const STIPEND_CURRENCY = "INR";

/**
 * At most 9 digits (999,999,999 a month), far inside the integers a JavaScript or Mongo number holds
 * exactly, so core-api never parses a stipend that loses precision.
 */
const STIPEND_MAX_DIGITS = 9;

/**
 * The question types the stipend input replaces: the text question older forms were seeded with and
 * the number question Form Builder seeds now. A stipend marked on any other type renders as it was built.
 */
const STIPEND_INPUT_TYPES: string[] = [QUESTION_TYPES.SHORT_TEXT, QUESTION_TYPES.LONG_TEXT, QUESTION_TYPES.NUMBER];

export const usesStipendInput = (question?: V2Question): boolean => Boolean(question && STIPEND_INPUT_TYPES.includes(question.questionType));

/** Digits only, without leading zeros. What typing keeps: any other character typed anywhere is dropped. */
export const keepDigits = (text: string): string => text.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

/**
 * Pasted or stored text as a whole monthly amount: cut at the first "." (dropping any decimals), then
 * digits only, so "18,000", "18000.50" and "INR 18,000.00 / month" all become "18000". Used for a paste,
 * a saved number and the submitted answer; typing never cuts, so a "." typed mid-number cannot drop the
 * digits after it.
 */
export const toStipendAmount = (value: unknown): string => {
  const text = typeof value === "number" && Number.isFinite(value) ? String(value) : typeof value === "string" ? value : "";
  return keepDigits(text.split(".")[0]);
};

/**
 * The stipend's submitted answer: the amount as a string of digits ("18000"), whatever the question's
 * type, so core-api reads the same shape from old text questions and new number questions. Null when empty.
 */
export const toStipendAnswer = (value: unknown): string | null => toStipendAmount(value) || null;

// A saved answer that is plainly an amount: digits, optionally grouped with commas ("18000", "18,000").
const PLAIN_AMOUNT = /^\d[\d,]*$/;

/**
 * A saved stipend for the read-only views. A number, or text that is plainly an amount, shows as its
 * digits; any other text an older form saved ("10000-15000", "18k", "Unpaid") shows exactly as written,
 * since pulling the digits out of it would show an amount the student never gave.
 */
export const toStipendDisplayValue = (answer: unknown): string => {
  if (typeof answer === "number") return toStipendAmount(answer);
  if (typeof answer !== "string") return "";
  return PLAIN_AMOUNT.test(answer.trim()) ? keepDigits(answer) : answer;
};

/** Puts the caret back after the value is rewritten, so a dropped keystroke or a paste does not throw it to the end. */
const placeCaret = (input: HTMLInputElement, position: number) => {
  window.requestAnimationFrame(() => {
    if (document.activeElement === input) input.setSelectionRange(position, position);
  });
};

interface StipendInputProps {
  // `id`, `value` and `onChange` are injected by the surrounding Form.Item.
  id?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

/** The amount input between the "INR" and "/ month" addons. */
const StipendInput: FunctionComponent<StipendInputProps> = ({ id, value, onChange, disabled }) => {
  // Typing keeps digits only: a "." or any other non-digit typed anywhere does nothing.
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const next = keepDigits(input.value);
    onChange?.(next);
    if (next !== input.value) {
      const digitsAfterCaret = input.value.slice(input.selectionStart ?? input.value.length).replace(/\D/g, "").length;
      placeCaret(input, Math.max(0, next.length - digitsAfterCaret));
    }
  };

  // A paste inserts the pasted text's whole amount (cut at the first ".", then digits) at the selection,
  // as many of its digits as fit in the length limit, which the browser does not apply to a value set here.
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const input = event.currentTarget;
    const current = input.value;
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? start;
    const before = current.slice(0, start).replace(/\D/g, "");
    const after = current.slice(end).replace(/\D/g, "");
    const room = Math.max(0, STIPEND_MAX_DIGITS - before.length - after.length);
    const pasted = event.clipboardData.getData("text").split(".")[0].replace(/\D/g, "").slice(0, room);
    const next = keepDigits(`${before}${pasted}${after}`);
    onChange?.(next);
    placeCaret(input, Math.max(0, next.length - after.length));
  };

  return (
    <Input
      id={id}
      value={value}
      onChange={handleChange}
      onPaste={handlePaste}
      disabled={disabled}
      inputMode="numeric"
      maxLength={STIPEND_MAX_DIGITS}
      autoComplete="off"
      addonBefore={STIPEND_CURRENCY}
      addonAfter="/ month"
      placeholder="Amount per month"
    />
  );
};

/** Required when the question is, and within the question's own min / max when it sets them (a number question). */
const buildStipendRules = (question: V2Question): Rule[] => {
  const rules: Rule[] = [];
  if (question.isRequired) rules.push({ required: true, message: "This field is required" });

  const { minValue, maxValue } = question.validation || {};
  if (typeof minValue === "number" || typeof maxValue === "number") {
    rules.push({
      validator: (_, value) => {
        const digits = toStipendAmount(value);
        if (!digits) return Promise.resolve();
        const amount = Number(digits);
        if (typeof minValue === "number" && amount < minValue) return Promise.reject(new Error(`Must be at least ${minValue}`));
        if (typeof maxValue === "number" && amount > maxValue) return Promise.reject(new Error(`Must be at most ${maxValue}`));
        return Promise.resolve();
      }
    });
  }
  return rules;
};

interface StipendFieldProps {
  question: V2Question;
  disabled?: boolean;
}

/**
 * An off-campus request's Stipend question as an amount per month: up to 9 digits, between an "INR"
 * prefix and a "/ month" suffix. The question's own label and required flag apply. Read-only views
 * show the saved answer as its amount (see `toStipendDisplayValue`).
 */
const StipendField: FunctionComponent<StipendFieldProps> = ({ question, disabled = false }) => (
  <Form.Item
    name={question.id}
    label={<FormLabel label={question.label} required={question.isRequired} description={question.description} />}
    rules={buildStipendRules(question)}
    // Read-only views show the saved answer like the other questions' read-only inputs, not greyed out.
    className={styles.disableTextInput}
  >
    <StipendInput disabled={disabled} />
  </Form.Item>
);

export default StipendField;
